'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  Calendar,
  ChevronRight,
  ClipboardList,
  Copy,
  Download,
  FileSpreadsheet,
  FileText,
  Heart,
  MoreHorizontal,
  Search,
  Share2,
  User as UserIcon,
  X,
} from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useApolloClient, useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { toast } from 'sonner';
import { clsx } from 'clsx';
import {
  ADD_FAVORITE_MUTATION,
  DOCUMENT_BY_NUMBER_QUERY,
  DOCUMENT_CHAIN_QUERY,
  DOCUMENT_PDF_URL_QUERY,
  DOCUMENT_QUERY,
  FAVORITES_QUERY,
  type Document,
  type DocumentLine,
  type MaterialTemplateItemInput,
  type RelatedDocument,
  DocumentType as DocumentTypeEnum,
  LineType,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import {
  useCart,
  RequestListBanner,
} from '@paeffgen/react/crm-documents/feature-material-requests';
import { SaveToProjectModal } from '@paeffgen/react/crm-documents/feature-favorites';
import { ArticleNameBlock, BottomSheet, Checkbox, Skeleton, Spinner, useNavHighlight } from '@paeffgen/react/crm-documents/ui';
import {
  exportLineItemsToCsv,
  formatCurrency,
  formatDate,
  formatPackageBreakdown,
  formatQuantity,
  getDocumentLineAnzahl,
  groupLinesByDelivery,
  groupLineItems,
  TYPE_BADGES,
  TYPE_SHORT,
  useOpenArticleInShop,
  type DeliveryBucket,
} from '@paeffgen/react/crm-documents/util';
import { usePdfShare } from './use-pdf-share';
import { OrderOfferButton } from './order-offer-button';

// ============================================================================
// Types & helpers
// ============================================================================

function lineKey(line: DocumentLine): string {
  return line.lineNumber;
}

// ============================================================================
// Context
// ============================================================================

interface MobileDocCtx {
  document: Document;
  lines: DocumentLine[];
  buckets: DeliveryBucket[];
  selected: Set<string>;
  toggleLine: (line: DocumentLine) => void;
  toggleBucket: (bucket: DeliveryBucket) => void;
  openPosition: (line: DocumentLine) => void;
  pushOpenByNumber: (docNumber: string) => Promise<void>;
  highlightArticleNumber: string | null;
  /** Order numbers that exist as documents — only these become a clickable link. */
  existingOrderNumbers: Set<string>;
}

const MobileDocCtxRef = createContext<MobileDocCtx | null>(null);

function useMobileDocCtx(): MobileDocCtx {
  const ctx = useContext(MobileDocCtxRef);
  if (!ctx) throw new Error('MobileDocCtx must be used inside MobileDocumentDetail');
  return ctx;
}

// ============================================================================
// Top-level component (provider + body)
// ============================================================================

interface MobileDocumentDetailProps {
  document: Document;
  onClose: () => void;
  /** Controlled selection state — owner is `DocumentDetailDialog` so the
   *  bottom-footer slot (sibling, outside this subtree) can read the same
   *  Set without context plumbing through `BottomSheet`. */
  selected: Set<string>;
  setSelected: (next: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  highlightArticleNumber?: string | null;
}

export function MobileDocumentDetail({ document, selected, setSelected, highlightArticleNumber }: MobileDocumentDetailProps) {
  const { adminQueryVars } = useAdmin();
  const router = useRouter();
  const searchParams = useSearchParams();
  const apollo = useApolloClient();

  const needsDetail = !document.lines || document.lines.length === 0;
  const { data: detailData, loading: detailLoading } = useQuery<{ document: Document }>(
    DOCUMENT_QUERY,
    {
      variables: { id: document.id, ...adminQueryVars },
      skip: !needsDetail,
      fetchPolicy: 'cache-first',
    }
  );

  const fullDocument =
    needsDetail && detailData?.document ? detailData.document : document;

  const allLines = fullDocument.lines ?? [];
  // Lieferscheine: hide lines without quantity (not actually delivered), but
  // keep TEXT hint lines — they carry no quantity yet appear on the PDF and
  // must stay visible (see document-detail.tsx for the desktop counterpart).
  const lines =
    fullDocument.type === DocumentTypeEnum.LIEFERSCHEIN
      ? allLines.filter((l) => l.quantity != null || l.lineType === LineType.TEXT)
      : allLines;

  const buckets = useMemo(() => groupLinesByDelivery(lines), [lines]);

  // Order numbers that exist as documents — only these get a clickable link in
  // the delivery cards (many Auftraege are not imported). Source: the document
  // chain (shared via Apollo cache with MobileRelatedDocuments).
  const { data: chainData } = useQuery<{ documentChain: RelatedDocument[] }>(
    DOCUMENT_CHAIN_QUERY,
    {
      variables: { documentId: fullDocument.id, ...adminQueryVars },
      fetchPolicy: 'cache-first',
      skip: !fullDocument.id,
    }
  );
  const existingOrderNumbers = useMemo(
    () =>
      new Set(
        (chainData?.documentChain ?? [])
          .filter((c) => c.type === DocumentTypeEnum.AUFTRAG)
          .map((c) => c.documentNumber)
      ),
    [chainData]
  );

  const [openPosLine, setOpenPosLine] = useState<DocumentLine | null>(null);

  const toggleLine = useCallback(
    (line: DocumentLine) => {
      setSelected((prev) => {
        const next = new Set(prev);
        const k = lineKey(line);
        if (next.has(k)) next.delete(k);
        else next.add(k);
        return next;
      });
    },
    [setSelected]
  );

  const toggleBucket = useCallback(
    (bucket: DeliveryBucket) => {
      setSelected((prev) => {
        const next = new Set(prev);
        const allOn = bucket.lines.every((l) => next.has(lineKey(l)));
        if (allOn) {
          bucket.lines.forEach((l) => next.delete(lineKey(l)));
        } else {
          bucket.lines.forEach((l) => next.add(lineKey(l)));
        }
        return next;
      });
    },
    [setSelected]
  );

  const pushOpenByNumber = useCallback(
    async (docNumber: string) => {
      try {
        const result = await apollo.query<{
          documentByNumber: { id: string } | null;
        }>({
          query: DOCUMENT_BY_NUMBER_QUERY,
          variables: { number: docNumber, ...adminQueryVars },
          fetchPolicy: 'network-only',
        });
        const doc = result.data?.documentByNumber;
        if (doc) {
          const params = new URLSearchParams(searchParams.toString());
          params.set('open', doc.id);
          router.push(`/belege?${params.toString()}`, { scroll: false });
        } else {
          toast.warning(`Beleg ${docNumber} nicht gefunden`);
        }
      } catch {
        toast.error(`Beleg ${docNumber} konnte nicht geladen werden`);
      }
    },
    [apollo, adminQueryVars, router, searchParams]
  );

  const pushOpen = useCallback(
    (id: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set('open', id);
      router.push(`/belege?${params.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  const ctx: MobileDocCtx = {
    document: fullDocument,
    lines,
    buckets,
    selected,
    toggleLine,
    toggleBucket,
    openPosition: setOpenPosLine,
    pushOpenByNumber,
    highlightArticleNumber: highlightArticleNumber ?? null,
    existingOrderNumbers,
  };

  // Zentraler Scroll-Orchestrator: bringt die hervorgehobene Position-Card
  // zuverlässig in den sichtbaren Bereich des BottomSheets — robust gegen
  // (a) Mehrfach-Treffer (Sammelrechnung mit demselben Artikel in 2 Lieferungen
  //     → nur das erste DOM-Match wird angesteuert)
  // (b) verzögert eintreffende Apollo-Daten (DocumentChain etc.) die das
  //     Layout nachträglich shiften → ResizeObserver triggert smooth-Korrektur.
  useEffect(() => {
    if (!highlightArticleNumber) return;
    // Aliasse für DOM-Zugriff, da der Parameter `document` (CRM-Document)
    // hier den globalen `document` (DOM) verdeckt.
    const dom = globalThis.document;
    let cancelled = false;
    let observer: ResizeObserver | null = null;
    let scrollTimer: number | null = null;
    let hardStopTimer: number | null = null;
    let setupRafId: number | null = null;

    const findFirstTarget = (): HTMLElement | null => {
      const sheet = dom.querySelector<HTMLElement>(
        '[role="dialog"][aria-modal="true"]',
      );
      if (!sheet) return null;
      return sheet.querySelector<HTMLElement>(
        `[data-line-article="${CSS.escape(highlightArticleNumber)}"]`,
      );
    };

    const findScrollContainer = (el: HTMLElement): HTMLElement | null => {
      let p: HTMLElement | null = el.parentElement;
      while (p) {
        const oy = window.getComputedStyle(p).overflowY;
        if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') return p;
        p = p.parentElement;
      }
      return null;
    };

    const doScroll = (behavior: ScrollBehavior) => {
      if (cancelled) return;
      const target = findFirstTarget();
      if (target) {
        target.scrollIntoView({ behavior, block: 'center' });
      }
    };

    const scheduleScroll = (behavior: ScrollBehavior, delay: number) => {
      if (scrollTimer != null) window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => doScroll(behavior), delay);
    };

    const setup = () => {
      if (cancelled) return;
      const target = findFirstTarget();
      if (!target) {
        // Target noch nicht im DOM (Sheet-Daten noch im Fly) → erneut probieren
        setupRafId = requestAnimationFrame(setup);
        return;
      }
      const container = findScrollContainer(target);
      if (!container) return;

      // Initial-Scroll: instant, sobald das Target sichtbar ist. Korrekturen
      // folgen via ResizeObserver auf den Content (firstElementChild).
      doScroll('auto');

      const content = container.firstElementChild as HTMLElement | null;
      if (content) {
        observer = new ResizeObserver(() => {
          scheduleScroll('smooth', 120);
        });
        observer.observe(content);
      }

      // Hard-Cap: nach 3 s spätestens loslassen (auch wenn Layout nie still wird).
      hardStopTimer = window.setTimeout(() => {
        observer?.disconnect();
        observer = null;
      }, 3000);
    };

    setupRafId = requestAnimationFrame(setup);

    return () => {
      cancelled = true;
      if (setupRafId != null) cancelAnimationFrame(setupRafId);
      if (scrollTimer != null) window.clearTimeout(scrollTimer);
      if (hardStopTimer != null) window.clearTimeout(hardStopTimer);
      observer?.disconnect();
    };
  }, [highlightArticleNumber, fullDocument.id]);

  if (detailLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <MobileDocCtxRef.Provider value={ctx}>
      <div className="px-4 pt-3 pb-6 space-y-4 bg-zinc-50">
        <MobilePillsRow document={fullDocument} />
        <MobileSummaryCard document={fullDocument} />
        <MobilePositionsSection />
        {fullDocument.id && (
          <MobileRelatedDocuments
            documentId={fullDocument.id}
            onOpen={pushOpen}
          />
        )}
      </div>
      {openPosLine && (
        <MobilePositionActionSheet
          line={openPosLine}
          onClose={() => setOpenPosLine(null)}
        />
      )}
    </MobileDocCtxRef.Provider>
  );
}

// ============================================================================
// Pills row
// ============================================================================

function MobilePillsRow({ document }: { document: Document }) {
  const { isAdmin, allCustomers } = useAdmin();
  const showDebitor = isAdmin && allCustomers;
  return (
    <div className="flex flex-wrap gap-2">
      {document.orderDate && (
        <Pill icon={<Calendar size={13} />} text={formatDate(document.orderDate)} />
      )}
      {showDebitor && document.customerName && (
        <Pill icon={<UserIcon size={13} />} text={document.customerName} />
      )}
      {document.systemId && <Pill mono prefix="IDM" text={document.systemId} />}
      {document.commission && (
        <Pill
          accent="komm"
          icon={<ClipboardList size={13} />}
          text={document.commission}
        />
      )}
    </div>
  );
}

function Pill({
  icon,
  prefix,
  text,
  mono,
  accent,
}: {
  icon?: ReactNode;
  prefix?: string;
  text: string;
  mono?: boolean;
  accent?: 'komm';
}) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full border text-xs font-medium',
        accent === 'komm'
          ? 'bg-orange-50 border-orange-200 text-orange-800'
          : 'bg-white border-gray-200 text-gray-700'
      )}
    >
      {icon}
      {prefix && <span className="text-gray-500 font-semibold">{prefix}</span>}
      <span className={clsx(mono && 'font-mono tabular-nums')}>{text}</span>
    </span>
  );
}

// ============================================================================
// Summary card
// ============================================================================

function MobileSummaryCard({ document }: { document: Document }) {
  if (
    document.type === DocumentTypeEnum.LIEFERSCHEIN ||
    document.grossTotal == null
  ) {
    return null;
  }
  const isCredit = document.type === DocumentTypeEnum.GUTSCHRIFT;
  const sign = isCredit ? -1 : 1;
  const gross = sign * (document.grossTotal ?? 0);
  const net = sign * (document.netTotal ?? 0);
  const vat = gross - net;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
          Brutto
        </span>
        <span
          className={clsx(
            'text-2xl font-extrabold tabular-nums',
            isCredit ? 'text-red-600' : 'text-gray-900'
          )}
        >
          {formatCurrency(gross)}
        </span>
      </div>
      <div className="border-t border-dashed border-gray-200 pt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 tabular-nums">
        <span>Netto {formatCurrency(net)}</span>
        <span>MwSt. (19%) {formatCurrency(vat)}</span>
      </div>
    </div>
  );
}

// ============================================================================
// Positions section: search + grouped or flat
// ============================================================================

function MobilePositionsSection() {
  const { lines, buckets } = useMobileDocCtx();
  const [search, setSearch] = useState('');

  const articles = useMemo(
    () => lines.filter((l) => l.lineType === LineType.ARTICLE),
    [lines]
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    return articles.filter(
      (l) =>
        l.description?.toLowerCase().includes(q) ||
        l.description2?.toLowerCase().includes(q) ||
        l.articleNumber?.toLowerCase().includes(q)
    );
  }, [search, articles]);

  // Browsing (no active search): interleave article cards with TEXT hint blocks
  // in line order (shared logic with the desktop view, see groupLineItems).
  const flatItems = useMemo(() => groupLineItems(lines), [lines]);

  if (articles.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-500">
        Keine Positionen verknüpft.
      </div>
    );
  }

  // Show flat layout when there's only the default bucket (Einzelrechnung)
  // OR when a search filter is active.
  const hasGrouping = buckets.some((b) => b.deliveryNumber != null);
  const flatMode = !hasGrouping || filtered != null;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
          Positionen{' '}
          <span className="text-gray-400 font-bold">{articles.length}</span>
        </h3>
        {articles.length >= 5 && (
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg pl-2 pr-1 h-8 max-w-[260px] flex-1">
            <Search size={14} className="text-gray-400 shrink-0" />
            <input
              type="text"
              placeholder="In Positionen suchen…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent text-sm outline-none min-w-0"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="w-6 h-6 grid place-items-center text-gray-400 hover:text-gray-600"
                aria-label="Suche leeren"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}
      </div>

      {flatMode ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-300">
          {filtered != null ? (
            filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">
                Keine Positionen gefunden.
              </div>
            ) : (
              filtered.map((line) => (
                <MobilePositionCard key={lineKey(line)} line={line} />
              ))
            )
          ) : (
            flatItems.map((item) =>
              item.kind === 'text-group' ? (
                <div
                  key={item.key}
                  className="px-3 py-2 bg-gray-50 text-xs text-gray-600 italic leading-snug space-y-0.5"
                >
                  {item.texts.map((t, i) => (
                    <div key={i}>{t}</div>
                  ))}
                </div>
              ) : (
                <MobilePositionCard key={lineKey(item.line)} line={item.line} />
              )
            )
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {buckets.map((b) => (
            <MobileDeliveryCard
              key={b.deliveryNumber ?? '__default__'}
              bucket={b}
              total={buckets.length}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Delivery accordion card
// ============================================================================

function MobileDeliveryCard({
  bucket,
  total,
}: {
  bucket: DeliveryBucket;
  total: number;
}) {
  const {
    selected,
    toggleBucket,
    pushOpenByNumber,
    highlightArticleNumber,
    existingOrderNumbers,
  } = useMobileDocCtx();
  // Auto-expand the bucket that contains the highlighted article — otherwise
  // the user lands on a collapsed list when jumping in from "Preisinformation".
  const containsHighlight =
    !!highlightArticleNumber &&
    bucket.lines.some((l) => l.articleNumber === highlightArticleNumber);
  const startOpen = total <= 2 || containsHighlight;
  const [open, setOpen] = useState(startOpen);
  const allOn = bucket.lines.every((l) => selected.has(lineKey(l)));
  // Enthält der Block überhaupt eine Auswahl? Signalisiert im zugeklappten
  // Zustand, dass darin ausgewählte Positionen stecken (sonst unsichtbar).
  const someOn = bucket.lines.some((l) => selected.has(lineKey(l)));

  const handleToggleAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleBucket(bucket);
  };

  return (
    <div
      className={clsx(
        'bg-white rounded-xl border overflow-hidden',
        open
          ? 'border-gray-300'
          : someOn
            ? 'border-orange-300'
            : 'border-gray-200'
      )}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={clsx(
          'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
          // Variante D: aktive (offene) Lieferung am Kopf mit grüner Unterkante markieren
          open && 'shadow-[inset_0_-2px_0_0_var(--color-status-delivery)]',
          // Zugeklappter Block mit Auswahl → selbe Auswahl-Hintergrundfarbe wie
          // eine ausgewählte Position, damit die Auswahl auch geschlossen sichtbar ist.
          !open && someOn && 'bg-orange-100'
        )}
      >
        <ChevronRight
          size={14}
          className={clsx(
            'text-gray-400 shrink-0 transition-transform',
            open && 'rotate-90'
          )}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-bold text-gray-900">
              Lieferung {bucket.deliveryNumber}
            </span>
            {bucket.deliveryDate && (
              <span className="text-xs text-gray-500 tabular-nums">
                Lieferdatum: {bucket.deliveryDate}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0 text-xs text-gray-500 mt-0.5">
            {bucket.orderNumber &&
              (existingOrderNumbers.has(bucket.orderNumber) ? (
                <span
                  className="hover:text-primary cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (bucket.orderNumber) pushOpenByNumber(bucket.orderNumber);
                  }}
                >
                  Auftrag {bucket.orderNumber}
                </span>
              ) : (
                <span>Auftrag {bucket.orderNumber}</span>
              ))}
            {bucket.orderNumber && bucket.orderDate && (
              <>
                <Sep />
                <span className="tabular-nums">
                  Auftragsdatum: {bucket.orderDate}
                </span>
              </>
            )}
            <Sep />
            <span>{bucket.lines.length} Pos.</span>
            {bucket.commission && (
              <>
                <Sep />
                <span className="text-orange-700 font-medium">
                  {bucket.commission}
                </span>
              </>
            )}
          </div>
        </div>
        <span className="text-sm font-bold text-gray-900 tabular-nums shrink-0">
          {formatCurrency(bucket.sum)}
        </span>
      </button>

      {open && (
        <>
          <div className="flex items-center justify-between gap-3 px-4 py-2 bg-gray-50 border-t border-gray-100">
            <span
              className="text-xs font-medium text-gray-600 select-none"
              onClick={handleToggleAll}
            >
              {allOn ? 'Auswahl entfernen' : 'Alle auswählen'}
            </span>
            <Checkbox checked={allOn} onClick={handleToggleAll} />
          </div>
          <div className="divide-y divide-gray-300">
            {groupLineItems(bucket.displayLines).map((item) =>
              item.kind === 'text-group' ? (
                <div
                  key={item.key}
                  className="px-3 py-2 bg-gray-50 text-xs text-gray-600 italic leading-snug space-y-0.5"
                >
                  {item.texts.map((t, i) => (
                    <div key={i}>{t}</div>
                  ))}
                </div>
              ) : (
                <MobilePositionCard key={lineKey(item.line)} line={item.line} />
              )
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Sep() {
  return <span className="text-gray-300">·</span>;
}

// ============================================================================
// Position card (one ARTICLE line)
// ============================================================================

function MobilePositionCard({ line }: { line: DocumentLine }) {
  const {
    document: doc,
    selected,
    toggleLine,
    openPosition,
    highlightArticleNumber,
  } = useMobileDocCtx();
  const isInvoice = doc.type !== DocumentTypeEnum.LIEFERSCHEIN;
  const isOn = selected.has(lineKey(line));
  const isHighlighted =
    !!highlightArticleNumber && line.articleNumber === highlightArticleNumber;
  const hasRabatt = (line.discount ?? 0) > 0;
  const breakdown = formatPackageBreakdown(line);

  // Scroll-Logik ist im MobileDocumentDetail-Root zentralisiert — nicht pro
  // Karte, weil bei Sammelrechnungen derselbe Artikel in mehreren Lieferungen
  // vorkommt und 2 simultane scrollIntoView-Calls eine Race-Condition
  // erzeugen. Hier markieren wir nur das Wurzel-Div mit data-line-article,
  // damit der zentrale Orchestrator das erste Match findet.

  return (
    <div
      data-line-article={line.articleNumber ?? ''}
      className={clsx(
        // Variante D: mehr vertikale Luft pro Position (14px horizontal / 15px vertikal)
        'px-3.5 py-[15px] cursor-pointer transition-colors',
        isOn
          ? 'bg-orange-100 hover:bg-orange-200/70'
          : isHighlighted
            ? 'bg-yellow-50 hover:bg-yellow-100'
            : 'bg-white hover:bg-gray-50/50'
      )}
      onClick={() => openPosition(line)}
    >
      <div className="space-y-1">
        <div className="flex items-start gap-3">
          <ArticleNameBlock
            className="flex-1"
            articleNumber={line.articleNumber ?? ''}
            description={line.description}
            description2={line.description2}
            manufacturer={line.manufacturer}
            descriptionClassName="text-sm font-bold text-gray-900 leading-snug"
            description2ClassName="text-xs text-gray-600 leading-snug"
            articleNumberClassName="text-[11px] font-mono text-gray-400"
            hideArticleNumber={!line.articleNumber}
            highlight
          />
          <Checkbox
            checked={isOn}
            onClick={(e) => {
              e.stopPropagation();
              toggleLine(line);
            }}
            className="mt-0.5"
          />
        </div>

        {/* Variante D: 3-Spalten-Preis-Grid als abgesetzte, gerundete Shelf */}
        <div className="mt-[11px] px-[11px] py-[9px] bg-gray-50 border border-gray-200 rounded-[10px] grid grid-cols-3 gap-2 items-start">
            <PriceCell label="MENGE">
              <div className="font-bold text-gray-900 tabular-nums">
                {line.quantity != null ? formatQuantity(line.quantity) : '—'}{' '}
                <span className="text-gray-500 font-normal">{line.unit}</span>
              </div>
              {breakdown && (
                <div className="text-[11px] text-gray-500 tabular-nums">
                  {breakdown}
                </div>
              )}
            </PriceCell>
            {isInvoice ? (
              <PriceCell label={`NETTO/${line.unit ?? ''}`}>
                <div className="font-bold text-gray-900 tabular-nums">
                  {line.netUnitPrice != null
                    ? formatCurrency(line.netUnitPrice)
                    : line.unitPrice != null
                      ? formatCurrency(line.unitPrice)
                      : '—'}
                </div>
                {hasRabatt && line.unitPrice != null && (
                  <div className="text-[11px] flex items-baseline gap-1.5 tabular-nums">
                    <span className="text-gray-400 line-through">
                      {formatCurrency(line.unitPrice)}
                    </span>
                    <span className="px-1 rounded bg-status-delivery-soft text-status-delivery font-semibold">
                      −{line.discount}%
                    </span>
                  </div>
                )}
              </PriceCell>
            ) : (
              <div />
            )}
            {isInvoice && line.amount != null ? (
              <PriceCell label="GESAMT" align="right">
                <div className="text-base font-extrabold text-gray-900 tabular-nums">
                  {formatCurrency(line.amount)}
                </div>
              </PriceCell>
            ) : (
              <div />
            )}
        </div>
      </div>
    </div>
  );
}

function PriceCell({
  label,
  align = 'left',
  children,
}: {
  label: string;
  align?: 'left' | 'right';
  children: ReactNode;
}) {
  return (
    <div className={clsx(align === 'right' && 'text-right')}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-0.5">
        {label}
      </div>
      {children}
    </div>
  );
}

// ============================================================================
// Position action sheet (long-press / tap → opens this)
// ============================================================================

function MobilePositionActionSheet({
  line,
  onClose,
}: {
  line: DocumentLine;
  onClose: () => void;
}) {
  const { addItem } = useCart();
  const { adminMutationVars } = useAdmin();
  const [addFavorite] = useMutation(ADD_FAVORITE_MUTATION, {
    refetchQueries: [{ query: FAVORITES_QUERY }],
  });
  const { open: openInShop } = useOpenArticleInShop();

  const handleAddToCart = async () => {
    if (!line.articleNumber) return;
    // Anfrage-Input ist die Anzahl (VE-Stück), nicht die Gesamtmenge.
    await addItem(line.articleNumber, getDocumentLineAnzahl(line));
    onClose();
  };
  const handleAddFavorite = async () => {
    if (!line.articleNumber) return;
    try {
      await addFavorite({
        variables: { articleNumber: line.articleNumber, ...adminMutationVars },
      });
      toast.success(`${line.articleNumber} zu Favoriten hinzugefügt`);
    } catch {
      toast.error('Konnte nicht zu Favoriten hinzugefügt werden');
    }
    onClose();
  };
  const handleCopyArticleNumber = async () => {
    if (!line.articleNumber) return;
    try {
      await navigator.clipboard.writeText(line.articleNumber);
      toast.success(`${line.articleNumber} kopiert`);
    } catch {
      toast.error('Kopieren nicht möglich');
    }
    onClose();
  };
  const handleOpenInShop = () => {
    if (!line.articleNumber) return;
    openInShop(line.articleNumber);
    onClose();
  };

  return (
    <BottomSheet
      open
      elevated
      onClose={onClose}
      ariaLabel={line.description ?? 'Position'}
      header={
        <div className="px-4 pt-2 pb-3 pr-12">
          <ArticleNameBlock
            articleNumber={line.articleNumber ?? ''}
            description={line.description}
            description2={line.description2}
            manufacturer={line.manufacturer}
            descriptionClassName="text-base font-bold text-gray-900"
            description2ClassName="text-sm text-gray-600"
            articleNumberClassName="text-xs font-mono text-gray-400"
            hideArticleNumber={!line.articleNumber}
            highlight
          />
        </div>
      }
    >
      <div className="py-2">
        <ActionRow
          icon={<ClipboardList size={18} />}
          label="Zu Anfrage hinzufügen"
          onClick={handleAddToCart}
          disabled={!line.articleNumber}
        />
        <ActionRow
          icon={<Heart size={18} />}
          label="Zu Favoriten"
          onClick={handleAddFavorite}
          disabled={!line.articleNumber}
        />
        <ActionRow
          icon={<Copy size={18} />}
          label="Artikelnummer kopieren"
          onClick={handleCopyArticleNumber}
          disabled={!line.articleNumber}
        />
        <ActionRow
          icon={<FileText size={18} />}
          label="Artikeldetails öffnen"
          onClick={handleOpenInShop}
          disabled={!line.articleNumber}
        />
      </div>
    </BottomSheet>
  );
}

function ActionRow({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'w-full flex items-center gap-3 px-5 py-3.5 text-left text-[15px] text-gray-900 active:bg-gray-100 transition-colors',
        disabled && 'opacity-40 cursor-not-allowed'
      )}
    >
      <span className="text-gray-500">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

// ============================================================================
// Bottom footer (rendered into <BottomSheet footer={…} /> slot)
// ============================================================================

interface MobileFooterProps {
  document: Document;
  /** Selected line keys — read from same context as the body. */
  selected: Set<string>;
  /** Auswahl-Setter (Owner: DocumentDetailDialog) — zum Auflösen der Auswahl
   *  nach „zur Anfrage". */
  setSelected: (
    next: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void;
}

/**
 * Footer is a sibling of the main body. It gets `selected` and `document`
 * passed down from `DocumentDetailDialog` (which is the place that owns
 * the BottomSheet host). To keep this self-contained we re-derive
 * actions here without going through the React context — that means
 * the body and footer cooperate via the parent rather than the context.
 */
export function MobileDocumentDetailFooter({
  document,
  selected,
  setSelected,
}: MobileFooterProps) {
  const { adminQueryVars, adminMutationVars, isSingleCustomerContext } = useAdmin();
  const { addItems } = useCart();
  const { markHighlight } = useNavHighlight();

  const [moreOpen, setMoreOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);

  // The list-query summary doesn't include `lines`, so we always go through
  // DOCUMENT_QUERY here — cache-first deduplicates with the body's load.
  const { data: detailData } = useQuery<{ document: Document }>(DOCUMENT_QUERY, {
    variables: { id: document.id, ...adminQueryVars },
    fetchPolicy: 'cache-first',
  });
  const fullDocument = detailData?.document ?? document;

  const [fetchPdfUrl, { loading: pdfLoading }] = useLazyQuery<{
    documentPdfUrl: string;
  }>(DOCUMENT_PDF_URL_QUERY, { fetchPolicy: 'network-only' });

  const articleLines = (fullDocument.lines ?? []).filter(
    (l) => l.lineType === LineType.ARTICLE && l.articleNumber && !l.isReturn
  );
  const targetLines =
    selected.size > 0
      ? articleLines.filter((l) => selected.has(l.lineNumber))
      : articleLines;

  const handleAddToCart = () => {
    const itemsToAdd = targetLines.map((line) => ({
      articleNumber: line.articleNumber!,
      // Anfrage-Input ist die Anzahl (VE-Stück), nicht die Gesamtmenge.
      quantity: getDocumentLineAnzahl(line),
    }));
    if (itemsToAdd.length === 0) {
      toast.warning('Keine Artikel auswählbar');
      return;
    }
    addItems(itemsToAdd);
    // Auswahl nach dem Hinzufügen auflösen — nur wenn explizit selektiert war
    // (bei „Alle zur Anfrage" gibt es nichts zu leeren).
    if (selected.size > 0) setSelected(new Set());
  };

  const handleDownloadPdf = async () => {
    if (!fullDocument.pdfKey) return;
    const result = await fetchPdfUrl({
      variables: { documentId: fullDocument.id, ...adminQueryVars },
    });
    if (result.data?.documentPdfUrl) {
      window.open(result.data.documentPdfUrl, '_blank');
    }
  };

  const { canShare, isSharing, sharePdf } = usePdfShare(adminQueryVars);
  const handleSharePdf = () => {
    if (!fullDocument.pdfKey) return;
    sharePdf(fullDocument.id, fullDocument.documentNumber);
  };

  const handleExportCsv = () => {
    if (targetLines.length === 0) return;
    exportLineItemsToCsv(
      [{ ...fullDocument, lines: targetLines }],
      `Positionen_${fullDocument.documentNumber}.csv`
    );
    setMoreOpen(false);
  };

  const templateItems: MaterialTemplateItemInput[] = targetLines.map((line) => ({
    articleNumber: line.articleNumber!,
    quantity: line.quantity ?? 1,
  }));

  const primaryLabel =
    selected.size > 0 ? `${selected.size} zur Anfrage` : 'Alle zur Anfrage';

  return (
    <div className="flex flex-col gap-2">
      {/* Angebot bestellen — prominent über der Aktionszeile, nur bei Angeboten (self-guarded) */}
      <OrderOfferButton document={fullDocument} fullWidth />
      {isSingleCustomerContext && <RequestListBanner />}
      <div className="flex items-stretch gap-2">
      <button
        type="button"
        onClick={handleAddToCart}
        className="flex-1 flex items-center justify-center gap-2 h-11 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary-hover transition-colors"
      >
        <ClipboardList size={16} />
        {primaryLabel}
      </button>
      <button
        type="button"
        onClick={handleDownloadPdf}
        disabled={!fullDocument.pdfKey || pdfLoading}
        className={clsx(
          'flex items-center justify-center gap-1 h-11 px-2.5 rounded-xl border text-sm font-medium transition-colors',
          fullDocument.pdfKey
            ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
        )}
      >
        {pdfLoading ? <Spinner size="sm" /> : <Download size={16} />}
        PDF
      </button>
      {canShare && fullDocument.pdfKey && (
        <button
          type="button"
          onClick={handleSharePdf}
          disabled={isSharing}
          aria-label="PDF teilen"
          className={clsx(
            'flex items-center justify-center h-11 w-11 rounded-xl border border-gray-300 bg-white transition-colors',
            isSharing
              ? 'text-primary cursor-not-allowed'
              : 'text-gray-700 hover:bg-gray-50'
          )}
        >
          {isSharing ? <Spinner size="sm" /> : <Share2 size={18} />}
        </button>
      )}
      <div className="relative">
        <button
          type="button"
          onClick={() => setMoreOpen((v) => !v)}
          className="flex items-center justify-center h-11 w-11 rounded-xl border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
          aria-label="Mehr Aktionen"
        >
          <MoreHorizontal size={18} />
        </button>
        {moreOpen && (
          <>
            <div
              className="fixed inset-0 z-[75]"
              onClick={() => setMoreOpen(false)}
              aria-hidden="true"
            />
            <div className="absolute bottom-full right-0 mb-2 z-[76] min-w-[240px] bg-white rounded-xl border border-gray-200 shadow-xl py-1 overflow-hidden">
              <MoreItem
                icon={<FileSpreadsheet size={16} />}
                label="CSV exportieren"
                onClick={handleExportCsv}
                disabled={targetLines.length === 0}
              />
              <MoreItem
                icon={<Copy size={16} />}
                label="Als Projekt speichern"
                onClick={() => {
                  setIsTemplateModalOpen(true);
                  setMoreOpen(false);
                }}
                disabled={targetLines.length === 0}
              />
            </div>
          </>
        )}
      </div>
      <SaveToProjectModal
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        items={templateItems}
        selectedCount={templateItems.length}
        initialName={fullDocument.documentNumber}
        queryVars={adminQueryVars}
        mutationVars={adminMutationVars}
        onSaved={() => {
          markHighlight('projekte');
          setIsTemplateModalOpen(false);
        }}
      />
      </div>
    </div>
  );
}

function MoreItem({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm text-gray-900 hover:bg-gray-50 transition-colors',
        disabled && 'opacity-40 cursor-not-allowed'
      )}
    >
      <span className="text-gray-500">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

// ============================================================================
// Related documents (mobile-only chain list)
// ============================================================================

function MobileRelatedDocuments({
  documentId,
  onOpen,
}: {
  documentId: string;
  onOpen: (id: string) => void;
}) {
  const { adminQueryVars } = useAdmin();
  const { data, loading } = useQuery<{ documentChain: RelatedDocument[] }>(
    DOCUMENT_CHAIN_QUERY,
    {
      variables: { documentId, ...adminQueryVars },
      fetchPolicy: 'cache-first',
    }
  );

  const related = useMemo(() => {
    const all = data?.documentChain ?? [];
    return [...all]
      .filter((d) => d.id !== documentId)
      .sort((a, b) => a.documentDate.localeCompare(b.documentDate));
  }, [data, documentId]);

  if (loading) {
    return <Skeleton className="h-24 w-full rounded-xl" />;
  }
  if (related.length === 0) return null;

  return (
    <section aria-label="Zugehörige Belege">
      <h3 className="px-1 mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        Zugehörige Belege
        <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-zinc-200 text-zinc-700 text-[11px] font-medium tabular-nums">
          {related.length}
        </span>
      </h3>
      <ul className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
        {related.map((doc) => (
          <li key={doc.id}>
            <button
              type="button"
              onClick={() => onOpen(doc.id)}
              aria-label={`Beleg ${doc.documentNumber} öffnen`}
              className="w-full flex items-center gap-3 px-3 py-3 hover:bg-zinc-50 active:bg-zinc-100 transition-colors"
            >
              <span
                className={clsx(
                  'shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold border',
                  TYPE_BADGES[doc.type]
                )}
              >
                {TYPE_SHORT[doc.type]}
              </span>
              <div className="flex-1 min-w-0 text-left">
                <p className="font-mono font-semibold text-zinc-900 truncate">
                  {doc.documentNumber}
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {formatDate(doc.documentDate, 'dd.MM.yy')}
                  {doc.lineCount > 0 && (
                    <>
                      {' '}
                      <span className="text-zinc-300">·</span> {doc.lineCount} Pos.
                    </>
                  )}
                </p>
              </div>
              {doc.type !== DocumentTypeEnum.LIEFERSCHEIN && doc.grossTotal != null && (
                <span className="shrink-0 font-semibold tabular-nums text-zinc-900">
                  {formatCurrency(doc.grossTotal)}
                </span>
              )}
              <ChevronRight size={18} className="shrink-0 text-zinc-400" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
